import { GridColDef, GridSortModel, GridCellParams } from '@mui/x-data-grid';
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DataGridModel, DataGrid } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { getAllGroupMessages } from 'src/shared/services/groupMessageService';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { Box } from '@mui/material';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { EmptyGridCell } from 'src/shared/components/atoms/emptyGridCell';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { GroupMessageResponse } from '../models/groupMessageResponse';
import { GroupMessageEmailSearchFilter } from '../models/groupMessageEmailSearchFilter';

type GroupMessageEmailGridProps = {
  loadingCallback: (loading: boolean) => void;
  searchFilter: GroupMessageEmailSearchFilter;
};

export const GroupMessageEmailGrid = (props: GroupMessageEmailGridProps) => {
  const { loadingCallback, searchFilter } = props;
  const { t } = useTranslation(['groupMessage']);
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const { scrollIntoView, scrollRef } = useScrollIntoView();

  const [rows, setRows] = useState<GroupMessageResponse[]>([]);
  const [data, setData] = React.useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: DEFAULT_PAGE_SIZE,
    page: DEFAULT_PAGE_INDEX,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'circular_id', sort: 'desc' }]);

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const onPageChange = (newPageIndex: number) => {
    updateData('page', newPageIndex);
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    updateData('page', 0);
    updateData('pageSize', newPageSize);
    scrollIntoView();
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
      loadingCallback(true);
    }

    try {
      const result = await getAllGroupMessages(data.page, data.pageSize, sortModel[0].field, sortModel[0].sort, searchFilter);
      if (isMounted()) {
        const memberResult = extendObjectWithId(result.data.results, 'circular_id');
        updateData('totalRows', result.data.totalItems);

        setTimeout(() => {
          if (isMounted()) setRows(memberResult);
        }, 100);
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) {
        updateData('loading', false);
        loadingCallback(false);
      }
    }
  }, [isMounted, loadingCallback, data.page, data.pageSize, sortModel, searchFilter]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [data.page, data.pageSize, fetchData, isMounted]);

  const onRowClick = (params) => {
    navigate(`${RoutesEnum.GroupMessageEmailEdit}/${params.row.id}`);
  };

  const columns: GridColDef[] = [
    {
      field: 'subject',
      headerName: t('groupMessage:tableHeaders.subject'),
      sortable: true,
      flex: 2,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.subject || t('groupMessage:noSubject')}</>;
      },
    },
    {
      field: 'sendDate',
      headerName: t('groupMessage:tableHeaders.sendDate'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        const { sendDate } = params.row;
        return <>{sendDate || t('groupMessage:tableHeaders.notSent')}</>;
      },
    },
    {
      field: 'sender',
      headerName: t('groupMessage:tableHeaders.sender'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.sender}</>;
      },
    },
    {
      field: 'created',
      headerName: t('groupMessage:tableHeaders.created'),
      type: 'number',
      sortable: false,
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.createdAt ? DateHelper.getDateTime(params.row.createdAt) : <EmptyGridCell />}</>;
      },
    },
    {
      field: 'circular_id',
      headerName: '',
      sortable: false,
      flex: 0.1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.circular_id}</>;
      },
    },
  ];

  return (
    <Box ref={scrollRef}>
      <DataGrid<GroupMessageResponse>
        columns={columns}
        rows={rows}
        autoHeight
        columnVisibilityModel={{
          circular_id: false,
        }}
        dataModel={data}
        checkboxSelection={false}
        onRowClick={onRowClick}
        disableSelectionOnClick
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          if (newSortModel.length) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort: sortModel[0].sort === 'asc' ? 'desc' : 'asc' }]);
          }
        }}
        selectionModel={[]}
        onSelectionModelChange={() => ConsoleHelper.log('')}
      />
    </Box>
  );
};
